import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wrench, AlertTriangle, CheckCircle2, Euro, ArrowRight, Receipt } from "lucide-react";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { DashboardAppuntamenti } from "@/components/DashboardAppuntamenti";
import { DashboardDaFirmare } from "@/components/DashboardDaFirmare";
import { DashboardGrafici } from "@/components/DashboardGrafici";
import { useAuth } from "@/context/AuthContext";
import { messaggioScheda, openWhatsApp } from "@/lib/whatsapp";
import { api, euro, dataIt, STATI, formatError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const Kpi = ({ label, value, icon: Icon, alert, testId, delay }) => (
  <div className={`fade-up rounded-lg border bg-white p-5 shadow-sm ${alert ? "border-rose-200" : "border-slate-200"}`} style={{ animationDelay: delay }}>
    <div className="flex items-start justify-between">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <Icon className={`h-5 w-5 ${alert ? "text-rose-500" : "text-orange-600"}`} />
    </div>
    <p data-testid={testId} className={`mt-4 font-mono text-3xl font-bold tracking-tight ${alert ? "text-rose-700" : "text-slate-900"}`}>{value}</p>
  </div>
);

const LowStock = ({ items }) => (
  <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="dashboard-low-stock">
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
      <h2 className="font-heading text-base font-bold text-slate-900">Scorte in esaurimento</h2>
      <Link to="/ricambi" className="text-xs font-semibold text-orange-600 hover:underline" data-testid="dashboard-goto-ricambi">Magazzino</Link>
    </div>
    <ul className="divide-y divide-slate-100">
      {items.map((r) => (
        <li key={r.id} className="flex items-center justify-between px-5 py-3 text-sm">
          <div>
            <p className="font-medium text-slate-900">{r.nome}</p>
            <p className="text-xs text-slate-500">{r.tipo} · soglia {r.soglia_minima}</p>
          </div>
          <span className="rounded-sm bg-rose-50 px-2 py-0.5 font-mono font-bold text-rose-700 ring-1 ring-rose-200">{r.quantita}</span>
        </li>
      ))}
      {items.length === 0 && <li className="px-5 py-10 text-center text-sm text-slate-500">Tutte le scorte sono sopra la soglia.</li>}
    </ul>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [negozio, setNegozio] = useState("");
  const [grafici, setGrafici] = useState(null);
  const [agg, setAgg] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const load = () => api.get("/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => {
    load();
    api.get("/impostazioni").then((r) => { setNegozio(r.data.nome_negozio || ""); setGrafici(r.data.grafici_dashboard || []); });
  }, []);
  const waText = (s, stato = s.stato) => messaggioScheda({ nome: s.cliente_nome.split(" ")[0], bici: s.bici_desc, numero: s.numero, stato, totale: s.totale, negozio });

  const changeStato = async (s, stato) => {
    try {
      await api.patch(`/schede/${s.id}/stato`, { stato });
      if (stato === "completato" && s.cliente_telefono) {
        toast.success("Bici pronta! Vuoi avvisare il cliente?", { duration: 10000, action: { label: "Avvisa su WhatsApp", onClick: () => openWhatsApp(s.cliente_telefono, waText(s, stato)) } });
      } else toast.success("Stato aggiornato");
      load();
      setAgg((n) => n + 1);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  if (!d) return <div className="py-20 text-center text-sm text-slate-500">Caricamento...</div>;
  const low = [...d.ricambi_bassi.map((r) => ({ ...r, tipo: "Ricambio" })), ...d.prodotti_bassi.map((r) => ({ ...r, tipo: "Prodotto" }))];

  return (
    <div>
      <PageHeader overline="Panoramica" title="Dashboard officina" />
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Kpi label="Lavori in corso" value={d.lavori_aperti} icon={Wrench} testId="kpi-lavori-aperti" delay="0ms" />
        <Kpi label="Scorte in esaurimento" value={low.length} icon={AlertTriangle} alert={low.length > 0} testId="kpi-scorte-basse" delay="60ms" />
        <Kpi label="Completate oggi" value={d.completate_oggi} icon={CheckCircle2} testId="kpi-completate-oggi" delay="120ms" />
        <Kpi label="Incassato officina (mese)" value={euro(d.fatturato_mese)} icon={Euro} testId="kpi-fatturato-mese" delay="180ms" />
        <Kpi label="Incasso banco (oggi)" value={euro(d.incasso_banco_oggi)} icon={Receipt} testId="kpi-incasso-banco" delay="240ms" />
      </div>
      {user?.puo_statistiche && grafici && <DashboardGrafici key={agg} scelti={grafici} />}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_360px]">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="dashboard-ultime-schede">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 className="font-heading text-base font-bold text-slate-900">Lavori attivi in officina</h2>
            <Link to="/schede" className="flex items-center gap-1 text-xs font-semibold text-orange-600 hover:underline" data-testid="dashboard-goto-schede">Tutte le schede <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="flex flex-wrap gap-2 border-b border-slate-100 px-5 py-3">
            {STATI.map((s) => (
              <span key={s.value} className="text-xs text-slate-500"><StatusBadge stato={s.value} /> <span className="ml-1 font-mono font-bold text-slate-900" data-testid={`dashboard-count-${s.value}`}>{d.stati[s.value]}</span></span>
            ))}
          </div>
          <ul className="divide-y divide-slate-100">
            {d.ultime_schede.map((s) => (
              <li key={s.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center">
                <button onClick={() => navigate(`/schede/${s.id}`)} className="flex-1 text-left" data-testid={`dashboard-scheda-${s.id}`}>
                  <span className="font-mono text-xs font-bold text-orange-600">#{s.numero}</span>
                  <p className="text-sm font-semibold text-slate-900">{s.cliente_nome} · <span className="font-normal text-slate-600">{s.bici_desc}</span></p>
                  <p className="text-xs text-slate-500">Ingresso {dataIt(s.data_ingresso)} · {euro(s.totale)}</p>
                </button>
                <WhatsAppButton compact telefono={s.cliente_telefono} text={waText(s)} testId={`dashboard-whatsapp-${s.id}`} />
                <Select value={s.stato} onValueChange={(v) => changeStato(s, v)}>
                  <SelectTrigger className="w-44" data-testid={`dashboard-stato-select-${s.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATI.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                </Select>
              </li>
            ))}
            {d.ultime_schede.length === 0 && <li className="px-5 py-12 text-center text-sm text-slate-500">Nessun lavoro attivo. Crea una nuova scheda per iniziare.</li>}
          </ul>
        </div>
        <div className="space-y-6">
          <DashboardAppuntamenti negozio={negozio} />
          <DashboardDaFirmare />
          <LowStock items={low} />
        </div>
      </div>
    </div>
  );
}
